/*
 * Copyright Mission 42 Ventures, Inc. All rights reserved.
 * Unauthorized copying, distribution, or use is prohibited.
 */

import { stringifyEntityRef, type Entity } from '@backstage/catalog-model';
import { KNOWN_MARKETPLACES, OVERRIDE_TARGETS, SERVER_OVERRIDES } from '../mcpConnect/servers';
import {
  MCP_CLIENT_IDS, type McpClientId, type McpEnvVar, type McpPlugin, type McpServerDescriptor, type McpTransport,
} from '../mcpConnect/types';

export type McpCatalogEntry = {
  descriptor: McpServerDescriptor;
  entityRef: string;
  description?: string;
  provider: string;
  tags: string[];
  experimental: boolean;
  owner: string;
};

export const MCP_SERVERS_FILTER = {
  kind: 'API',
  'spec.type': 'mcp-server',
  'metadata.annotations.m42.io/mcp-vetted': 'true',
} as const;

type McpSpec = {
  type?: unknown;
  lifecycle?: string;
  owner?: string;
  remotes?: Array<{ type?: string; url?: string }>;
  local?: { command?: unknown; args?: unknown; env?: unknown; requires?: unknown };
};

/* Mirrors the processor's spec.local rules (mcpCatalogProcessor.ts) so the grid
 * never offers a command the catalog would have rejected. */
const LOCAL_COMMAND = /^[a-z0-9][a-z0-9._-]*$/;
const LOCAL_ARG = /^[A-Za-z0-9._@=:/+-]+$/;
const ENV_NAME = /^[A-Z][A-Z0-9_]*$/;

const isClient = (id: string): id is McpClientId =>
  (MCP_CLIENT_IDS as readonly string[]).includes(id);

function pluginFor(id: string | undefined): McpPlugin | undefined {
  if (!id) return undefined;
  const [name, marketplace] = id.split('@');
  const known = marketplace && Object.prototype.hasOwnProperty.call(KNOWN_MARKETPLACES, marketplace)
    ? KNOWN_MARKETPLACES[marketplace] : undefined;
  // An unknown marketplace has no source to add: offering the Plugin setup
  // would hand out a command that cannot work.
  return known ? { id, source: known.source, guideUrl: `${known.guideBase}${name}` } : undefined;
}

function usableRemote(remote: { type?: string; url?: string }): boolean {
  if (remote.type !== 'streamable-http' || !remote.url) return false;
  try {
    const url = new URL(remote.url);
    return url.protocol === 'https:' && url.hostname.length > 0;
  } catch {
    return false;
  }
}

function usableLocal(local: McpSpec['local']): McpTransport | undefined {
  if (!local || typeof local !== 'object') return undefined;
  const { command, args, env, requires } = local;
  if (typeof command !== 'string' || !LOCAL_COMMAND.test(command)) return undefined;
  if (!Array.isArray(args) || args.length === 0 ||
      !args.every(a => typeof a === 'string' && LOCAL_ARG.test(a))) return undefined;
  const vars: McpEnvVar[] = [];
  for (const e of Array.isArray(env) ? env : []) {
    const v = (e ?? {}) as Record<string, unknown>;
    if (typeof v.name !== 'string' || !ENV_NAME.test(v.name)) return undefined;
    if (v.value !== undefined && (typeof v.value !== 'string' || !LOCAL_ARG.test(v.value))) return undefined;
    vars.push({
      name: v.name,
      ...(typeof v.description === 'string' ? { description: v.description } : {}),
      ...(typeof v.required === 'boolean' ? { required: v.required } : {}),
      ...(typeof v.value === 'string' ? { value: v.value } : {}),
    });
  }
  return {
    kind: 'local', command, args: args as string[],
    ...(vars.length ? { env: vars } : {}),
    ...(typeof requires === 'string' && requires.trim() ? { requires: requires.trim() } : {}),
  };
}

/** A vetted catalog entity → what the page and panel render. Undefined when the
 * entry has nothing this build can install (no https streamable-http remote and
 * no usable spec.local, or only clients this build doesn't know). */
export function entityToEntry(entity: Entity): McpCatalogEntry | undefined {
  const spec = (entity.spec ?? {}) as McpSpec;
  const annotations = entity.metadata.annotations ?? {};
  if (String(spec.type).toLowerCase() !== 'mcp-server' ||
      annotations['m42.io/mcp-vetted'] !== 'true') return undefined;
  const raw = spec.remotes?.find(usableRemote)?.url;
  const clients = (annotations['m42.io/mcp-clients'] ?? '')
    .split(',')
    .map(c => c.trim())
    .filter(isClient);
  const transport: McpTransport | undefined = raw
    ? { kind: 'remote', url: new URL(raw).href } : usableLocal(spec.local);
  if (!transport || clients.length === 0) return undefined;

  const name = entity.metadata.name;
  const link = entity.metadata.links?.[0];
  let docLink: McpServerDescriptor['docLink'];
  if (link) {
    try {
      if (new URL(link.url).protocol === 'https:') {
        docLink = { label: link.title ?? 'Setup guide', href: link.url };
      }
    } catch {
      // Invalid documentation links are omitted.
    }
  }
  const plugin = pluginFor(annotations['m42.io/mcp-plugin']);
  const target = Object.prototype.hasOwnProperty.call(OVERRIDE_TARGETS, name)
    ? OVERRIDE_TARGETS[name] : undefined;
  return {
    descriptor: {
      name,
      title: entity.metadata.title ?? name,
      transport,
      clients,
      plugin,
      docLink,
      overrides: target && transport.kind === 'remote' && transport.url === target.url && plugin?.id === target.plugin
        ? SERVER_OVERRIDES[name] : undefined,
    },
    entityRef: stringifyEntityRef(entity),
    description: entity.metadata.description,
    provider: annotations['m42.io/mcp-provider'] ?? '',
    tags: entity.metadata.tags ?? [],
    experimental: spec.lifecycle === 'experimental',
    owner: spec.owner ?? '',
  };
}
